import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { funilPublicoHabilitado } from '@/config/funil';
import { Button } from '@/components/atoms';
import { ApiError } from '@/services/api';
import { authApi } from '@/services/auth';
import { useAuth } from '@/store/useAuth';
import { useBrand } from '@/store/useBrand';

import styles from './Login.module.css';

/**
 * O porquê de o login não dizer mais "credenciais inválidas" para tudo.
 *
 * O `catch` tratava só o 429 e mandava TODO o resto para a mesma frase. Quem
 * levava a culpa era sempre a senha do operador — inclusive quando o problema
 * era o oposto: a senha estava certa e o backend estava dizendo, com todas as
 * letras, o que fazer a seguir. Os dois casos que mais doem num SaaS
 * self-service:
 *
 *   402 → "trial expirado — assine um plano para continuar operando"
 *   403 → "Conta suspensa. Fale com o administrador da plataforma."
 *
 * Ou seja: o cliente que ia PAGAR era mandado embora achando que tinha errado a
 * senha, e ficava tentando de novo até desistir. Medido nesta auditoria: um 404
 * de configuração de ambiente também virava "credenciais inválidas", e não há
 * senha no mundo que resolva um 404.
 *
 * A regra agora é: quando o servidor explica, a tela repete a explicação dele;
 * quando não explica, a tela diz a verdade do que sabe — e "não consegui falar
 * com o servidor" nunca mais é confundido com "sua senha está errada".
 */
function mensagemDeEntrada(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
  }
  const detalhe = (err.body as { detail?: unknown } | undefined)?.detail;
  const doServidor = typeof detalhe === 'string' ? detalhe.trim() : '';

  switch (err.status) {
    case 401:
      return 'Usuário ou senha incorretos.';
    case 429:
      return 'Muitas tentativas seguidas. Aguarde um minuto e tente de novo.';
    // 402 e 403 são os únicos casos em que o servidor sabe mais que a tela: ele
    // conhece o estado da conta (trial vencido, suspensa) e já vem com a frase
    // que diz o que fazer. Descartá-la era jogar fora a única instrução útil.
    case 402:
      return doServidor || 'Seu período de avaliação terminou. Assine um plano para continuar.';
    case 403:
      return doServidor || 'Esta conta está suspensa. Fale com o administrador da plataforma.';
    default:
      if (err.status >= 500) {
        return 'O servidor falhou ao responder. Tente de novo em instantes.';
      }
      return doServidor || `Não foi possível entrar (erro ${err.status}).`;
  }
}

/**
 * A-10 (perda 2, o outro lado do fio): o guard de sessão guarda em
 * `state.from` para onde a pessoa queria ir. Só se honra caminho INTERNO
 * (começa com uma barra, não com duas) — nunca URL absoluta, nunca a própria
 * tela de login. Qualquer outra coisa cai na home, como antes.
 */
function destinoSeguro(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  if (!v.startsWith('/') || v.startsWith('//')) return null;
  if (v === '/login' || v.startsWith('/login?')) return null;
  return v;
}

export function Login() {
  const { t } = useTranslation(['common']);
  const navigate = useNavigate();
  const location = useLocation();
  const destino = destinoSeguro((location.state as { from?: unknown } | null)?.from);
  const setAuth = useAuth((s) => s.setAuth);
  const brand = useBrand((s) => s.brand);
  const funilHabilitado = funilPublicoHabilitado();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.login(username.trim(), password);
      setAuth(res.access_token, res.username, res.role, res.must_change_password);
      // A marca é POR CLIENTE e o boot leu a marca pública (a da casa, ou a do
      // host dedicado). Com o token em mãos o tenant é conhecido com precisão —
      // num host compartilhado esta é a única forma de o cliente ver a marca
      // dele em vez da neutra.
      void useBrand.getState().loadBrand();
      navigate(res.must_change_password ? '/trocar-senha' : (destino ?? '/'), { replace: true });
    } catch (err) {
      setError(mensagemDeEntrada(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.root}>
      <form className={styles.card} onSubmit={handleSubmit}>
        <div className={styles.brand}>
          {brand.logo_url ? (
            <img className={styles.logo} src={brand.logo_url} alt={brand.name} />
          ) : (
            <>
              <span className={styles.brandMark}>{brand.name}</span>
              {brand.sub && <span className={styles.brandSub}>{brand.sub}</span>}
            </>
          )}
        </div>
        <p className={styles.tagline}>{brand.tagline}</p>

        <label className={styles.field}>
          <span>Usuário</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </label>

        <label className={styles.field}>
          <span>Senha</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>

        {error && <p className={styles.error}>{error}</p>}

        <Button type="submit" loading={loading} className={styles.submit}>
          Entrar
        </Button>

        {funilHabilitado && (
          <div className={styles.footer}>
            <span>{t('common:labels.no_account', 'Ainda não tem conta?')}</span>
            <Link to="/signup" className={styles.link}>
              {t('common:actions.create_account', 'Criar conta')}
            </Link>
          </div>
        )}
      </form>
    </div>
  );
}
